import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Plus, Trash2, Save, CreditCard, Check, AlertCircle, Loader2, RefreshCw, ExternalLink } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Switch } from '../components/ui/switch';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import ThemeToggle from '../components/ThemeToggle';
import apiService from '../services/api';
import BrandLogo from '../components/BrandLogo';

const AdminPaymentSettingsPage = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [message, setMessage] = useState(null);
  const [plans, setPlans] = useState([]);
  const [updatedAt, setUpdatedAt] = useState(null);

  useEffect(() => {
    loadPaymentSettings();
  }, []);

  const loadPaymentSettings = async () => {
    setIsLoading(true);
    try {
      const response = await apiService.request('/payment-settings/admin');
      setPlans(response.plans || []);
      setUpdatedAt(response.updated_at);
    } catch (error) {
      console.error('Error loading payment settings:', error);
      setMessage({ type: 'error', text: 'Failed to load payment settings' });
      // Set defaults
      setPlans([
        {
          plan_id: 'pro',
          plan_name: 'Pro',
          monthly_url: '',
          annual_url: '',
          is_active: true
        }
      ]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await apiService.request('/payment-settings', {
        method: 'PUT',
        body: JSON.stringify({ plans }),
      });
      setMessage({ type: 'success', text: 'Payment settings saved successfully!' });
      setTimeout(() => setMessage(null), 3000);
    } catch (error) {
      console.error('Error saving payment settings:', error);
      setMessage({ type: 'error', text: 'Failed to save payment settings' });
    } finally {
      setIsSaving(false);
    }
  };

  const handlePlanChange = (index, field, value) => {
    const updatedPlans = [...plans];
    updatedPlans[index] = { ...updatedPlans[index], [field]: value };
    setPlans(updatedPlans);
  };

  const handleAddPlan = () => {
    const newId = `plan_${Date.now()}`;
    setPlans([...plans, {
      plan_id: newId,
      plan_name: 'New Plan',
      monthly_url: '',
      annual_url: '',
      is_active: true,
      price: '',
      period: '',
      badge: '',
      features: [],
      limitations: [],
      fine_print: '',
      tier: 'pro',
      display_order: plans.length + 1,
    }]);
  };

  // For any string field where the user pastes newline-separated bullets
  // (features / limitations), convert to and from an array cleanly.
  const linesToArray = (text) =>
    (text || '')
      .split('\n')
      .map((s) => s.trim())
      .filter((s) => s.length > 0);
  const arrayToLines = (arr) => (Array.isArray(arr) ? arr.join('\n') : '');

  const handleRemovePlan = (index) => {
    if (plans.length <= 1) {
      setMessage({ type: 'error', text: 'You must have at least one payment plan' });
      return;
    }
    const updatedPlans = plans.filter((_, i) => i !== index);
    setPlans(updatedPlans);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-slate-100 dark:from-slate-900 dark:via-slate-800 dark:to-slate-900 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-slate-100 dark:from-slate-900 dark:via-slate-800 dark:to-slate-900">
      {/* Header */}
      <header className="border-b border-slate-200 dark:border-slate-700 bg-white/80 dark:bg-slate-900/80 backdrop-blur-md sticky top-0 z-40">
        <div className="container mx-auto px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between">
          <div className="flex items-center gap-2 sm:gap-3 cursor-pointer" onClick={() => navigate('/')}>
            <BrandLogo size="responsive" data-testid="brand-logo-header" />
            <span className="text-xl sm:text-2xl font-bold bg-gradient-to-r from-blue-600 to-blue-800 dark:from-blue-400 dark:to-blue-600 bg-clip-text text-transparent">
              KaiCheckIt - Admin
            </span>
          </div>
          <div className="flex items-center gap-2">
            <ThemeToggle />
            <Button
              onClick={() => navigate('/')}
              variant="outline"
              size="sm"
              className="flex items-center gap-1"
            >
              <ArrowLeft className="w-4 h-4" />
              <span className="hidden sm:inline">Back</span>
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-4 sm:px-6 py-6 sm:py-8">
        <div className="max-w-4xl mx-auto space-y-6">
          {/* Page Title */}
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-slate-800 dark:text-slate-100">
                Payment Settings
              </h1>
              <p className="text-slate-600 dark:text-slate-400 mt-1">
                Manage payment URLs for subscription plans
              </p>
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={loadPaymentSettings}
                className="flex items-center gap-2"
                data-testid="refresh-payment-settings-btn"
              >
                <RefreshCw className="w-4 h-4" />
                Refresh
              </Button>
              <Button
                onClick={handleSave}
                disabled={isSaving}
                className="bg-blue-600 hover:bg-blue-700 text-white"
                data-testid="save-payment-settings-btn"
              >
                {isSaving ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <>
                    <Save className="w-4 h-4 mr-2" />
                    Save All
                  </>
                )}
              </Button>
            </div>
          </div>

          {/* Message Alert */}
          {message && (
            <Alert className={message.type === 'success' 
              ? 'bg-green-50 dark:bg-green-900/30 border-green-200 dark:border-green-700' 
              : 'bg-red-50 dark:bg-red-900/30 border-red-200 dark:border-red-700'
            }>
              {message.type === 'success' ? (
                <Check className="h-4 w-4 text-green-600 dark:text-green-400" />
              ) : (
                <AlertCircle className="h-4 w-4 text-red-600 dark:text-red-400" />
              )}
              <AlertDescription className={message.type === 'success' 
                ? 'text-green-800 dark:text-green-200' 
                : 'text-red-800 dark:text-red-200'
              }>
                {message.text}
              </AlertDescription>
            </Alert>
          )}

          {/* Last Updated */}
          {updatedAt && (
            <p className="text-sm text-slate-500 dark:text-slate-400">
              Last updated: {new Date(updatedAt).toLocaleString()}
            </p>
          )}

          {/* Payment Plans */}
          <div className="space-y-4">
            {plans.map((plan, index) => (
              <Card key={plan.plan_id} className="border-slate-200 dark:border-slate-700">
                <CardHeader className="pb-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-gradient-to-br from-amber-500 to-amber-600 rounded-lg flex items-center justify-center">
                        <CreditCard className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <CardTitle className="text-lg text-slate-800 dark:text-slate-100">
                          {plan.plan_name}
                        </CardTitle>
                        <CardDescription className="text-slate-500 dark:text-slate-400">
                          ID: {plan.plan_id}
                        </CardDescription>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="flex items-center gap-2">
                        <Label className="text-sm text-slate-600 dark:text-slate-400">Active</Label>
                        <Switch
                          checked={plan.is_active}
                          onCheckedChange={(checked) => handlePlanChange(index, 'is_active', checked)}
                        />
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleRemovePlan(index)}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/30"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Plan Name */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <Label className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-2 block">
                        Plan Name
                      </Label>
                      <Input
                        value={plan.plan_name}
                        onChange={(e) => handlePlanChange(index, 'plan_name', e.target.value)}
                        placeholder="e.g., Pro"
                        className="dark:bg-slate-800 dark:border-slate-600 dark:text-slate-100"
                        data-testid={`plan-name-input-${plan.plan_id}`}
                      />
                    </div>
                    <div>
                      <Label className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-2 block">
                        Plan ID
                      </Label>
                      <Input
                        value={plan.plan_id}
                        onChange={(e) => handlePlanChange(index, 'plan_id', e.target.value.toLowerCase().replace(/\s+/g, '_'))}
                        placeholder="e.g., pro"
                        className="dark:bg-slate-800 dark:border-slate-600 dark:text-slate-100 font-mono"
                        data-testid={`plan-id-input-${plan.plan_id}`}
                      />
                    </div>
                  </div>

                  {/* Payment URLs */}
                  <div className="space-y-3">
                    <div>
                      <Label className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-2 block">
                        Monthly Payment URL
                      </Label>
                      <div className="flex gap-2">
                        <Input
                          value={plan.monthly_url || ''}
                          onChange={(e) => handlePlanChange(index, 'monthly_url', e.target.value)}
                          placeholder="Paste any payment link — Stripe / PayPal / eZee / Lumin / Razorpay …"
                          className="flex-1 dark:bg-slate-800 dark:border-slate-600 dark:text-slate-100"
                          data-testid={`monthly-url-input-${plan.plan_id}`}
                        />
                        {plan.monthly_url && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => window.open(plan.monthly_url, '_blank')}
                            className="px-3"
                            data-testid={`monthly-url-external-link-${plan.plan_id}`}
                          >
                            <ExternalLink className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </div>

                    <div>
                      <Label className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-2 block">
                        Annual Payment URL
                      </Label>
                      <div className="flex gap-2">
                        <Input
                          value={plan.annual_url || ''}
                          onChange={(e) => handlePlanChange(index, 'annual_url', e.target.value)}
                          placeholder="Optional — paste any provider's annual checkout link"
                          className="flex-1 dark:bg-slate-800 dark:border-slate-600 dark:text-slate-100"
                          data-testid={`annual-url-input-${plan.plan_id}`}
                        />
                        {plan.annual_url && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => window.open(plan.annual_url, '_blank')}
                            className="px-3"
                            data-testid={`annual-url-external-link-${plan.plan_id}`}
                          >
                            <ExternalLink className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Pricing display */}
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <div>
                      <Label className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-2 block">
                        Price (display)
                      </Label>
                      <Input
                        value={plan.price || ''}
                        onChange={(e) => handlePlanChange(index, 'price', e.target.value)}
                        placeholder="$27"
                        className="dark:bg-slate-800 dark:border-slate-600 dark:text-slate-100"
                        data-testid={`plan-price-input-${plan.plan_id}`}
                      />
                    </div>
                    <div>
                      <Label className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-2 block">
                        Period (display)
                      </Label>
                      <Input
                        value={plan.period || ''}
                        onChange={(e) => handlePlanChange(index, 'period', e.target.value)}
                        placeholder="one-time / monthly / per year"
                        className="dark:bg-slate-800 dark:border-slate-600 dark:text-slate-100"
                        data-testid={`plan-period-input-${plan.plan_id}`}
                      />
                    </div>
                    <div>
                      <Label className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-2 block">
                        Badge (optional)
                      </Label>
                      <Input
                        value={plan.badge || ''}
                        onChange={(e) => handlePlanChange(index, 'badge', e.target.value)}
                        placeholder="Best Value / Most Popular"
                        className="dark:bg-slate-800 dark:border-slate-600 dark:text-slate-100"
                        data-testid={`plan-badge-input-${plan.plan_id}`}
                      />
                    </div>
                  </div>

                  {/* Tier + display order */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <Label className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-2 block">
                        Grants Subscription Tier
                      </Label>
                      <Select
                        value={plan.tier || 'pro'}
                        onValueChange={(val) => handlePlanChange(index, 'tier', val)}
                      >
                        <SelectTrigger
                          className="dark:bg-slate-800 dark:border-slate-600 dark:text-slate-100"
                          data-testid={`plan-tier-select-${plan.plan_id}`}
                        >
                          <SelectValue placeholder="Select tier" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="pro">pro</SelectItem>
                          <SelectItem value="premium">premium</SelectItem>
                          <SelectItem value="enterprise">enterprise</SelectItem>
                        </SelectContent>
                      </Select>
                      <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-1">
                        Set on <code>users.subscription_tier</code> after successful payment
                      </p>
                    </div>
                    <div>
                      <Label className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-2 block">
                        Display Order
                      </Label>
                      <Input
                        type="number"
                        value={plan.display_order ?? 0}
                        onChange={(e) => handlePlanChange(index, 'display_order', parseInt(e.target.value || '0', 10))}
                        placeholder="1"
                        className="dark:bg-slate-800 dark:border-slate-600 dark:text-slate-100"
                        data-testid={`plan-order-input-${plan.plan_id}`}
                      />
                    </div>
                  </div>

                  {/* Features */}
                  <div>
                    <Label className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-2 block">
                      Features (one per line)
                    </Label>
                    <Textarea
                      value={arrayToLines(plan.features)}
                      onChange={(e) => handlePlanChange(index, 'features', linesToArray(e.target.value))}
                      placeholder={"Unlimited verifications\nPersistent history\nPriority support"}
                      className="min-h-[110px] dark:bg-slate-800 dark:border-slate-600 dark:text-slate-100"
                      data-testid={`plan-features-input-${plan.plan_id}`}
                    />
                  </div>

                  {/* Limitations */}
                  <div>
                    <Label className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-2 block">
                      Limitations (one per line, optional)
                    </Label>
                    <Textarea
                      value={arrayToLines(plan.limitations)}
                      onChange={(e) => handlePlanChange(index, 'limitations', linesToArray(e.target.value))}
                      placeholder={"No API access\nSingle user only"}
                      className="min-h-[70px] dark:bg-slate-800 dark:border-slate-600 dark:text-slate-100"
                      data-testid={`plan-limitations-input-${plan.plan_id}`}
                    />
                  </div>

                  {/* Fine print */}
                  <div>
                    <Label className="text-sm font-medium text-slate-700 dark:text-slate-300 mb-2 block">
                      Fine Print (optional)
                    </Label>
                    <Input
                      value={plan.fine_print || ''}
                      onChange={(e) => handlePlanChange(index, 'fine_print', e.target.value)}
                      placeholder="Lifetime access for early adopters"
                      className="dark:bg-slate-800 dark:border-slate-600 dark:text-slate-100"
                      data-testid={`plan-fine-print-input-${plan.plan_id}`}
                    />
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Add Plan Button */}
          <Button
            variant="outline"
            onClick={handleAddPlan}
            className="w-full py-6 border-dashed border-2 text-slate-600 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200"
            data-testid="add-payment-plan-btn"
          >
            <Plus className="w-5 h-5 mr-2" />
            Add Payment Plan
          </Button>

          {/* Info Card */}
          <Card className="border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50">
            <CardHeader>
              <CardTitle className="text-lg text-slate-800 dark:text-slate-100">Setup Instructions</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-sm text-slate-600 dark:text-slate-400">
              <p>
                <strong className="text-slate-700 dark:text-slate-300">Vendor-agnostic:</strong> Paste any payment provider's checkout / product link into the Payment URL fields — Stripe, PayPal, eZeePayments, Lumin, Razorpay, Paddle, LemonSqueezy, or any other. The site just redirects the buyer to the URL you provide.
              </p>
              <p>
                <strong className="text-slate-700 dark:text-slate-300">Plan ID:</strong> A unique slug (e.g., <code className="bg-slate-200 dark:bg-slate-700 px-1 rounded">pro</code>, <code className="bg-slate-200 dark:bg-slate-700 px-1 rounded">premium</code>, <code className="bg-slate-200 dark:bg-slate-700 px-1 rounded">team</code>). Send this in your provider's postback query string as <code className="bg-slate-200 dark:bg-slate-700 px-1 rounded">?plan_id=your_slug</code> so the webhook grants the correct tier. If omitted, the webhook falls back to tier <code className="bg-slate-200 dark:bg-slate-700 px-1 rounded">pro</code>.
              </p>
              <p>
                <strong className="text-slate-700 dark:text-slate-300">Grants Subscription Tier:</strong> The <code className="bg-slate-200 dark:bg-slate-700 px-1 rounded">users.subscription_tier</code> value assigned after successful payment. Pick from <code className="bg-slate-200 dark:bg-slate-700 px-1 rounded">pro</code> / <code className="bg-slate-200 dark:bg-slate-700 px-1 rounded">premium</code> / <code className="bg-slate-200 dark:bg-slate-700 px-1 rounded">enterprise</code>. All three currently unlock premium features.
              </p>
              <p>
                <strong className="text-slate-700 dark:text-slate-300">Webhook (Post-Back) URL:</strong> <code className="bg-slate-200 dark:bg-slate-700 px-1 rounded">https://app.kaicheckit.com/api/upgrade/webhook</code> — configure this in your payment provider's dashboard.
              </p>
              <p>
                <strong className="text-slate-700 dark:text-slate-300">Return URL:</strong> <code className="bg-slate-200 dark:bg-slate-700 px-1 rounded">https://app.kaicheckit.com/upgrade/success</code>
              </p>
              <p>
                <strong className="text-slate-700 dark:text-slate-300">Display Order:</strong> Ascending — lowest number appears first (Free is always first, then paid plans by <code className="bg-slate-200 dark:bg-slate-700 px-1 rounded">display_order</code>).
              </p>
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
};

export default AdminPaymentSettingsPage;
